/* eslint-disable max-lines */
import { useState } from "react"

import { useTranslation } from "react-i18next"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"

import type { FactCheckResult } from "@/api/fact-check-api"

import {
  AlertCircle,
  Book,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Eye,
  FileText,
  HelpCircle,
  Lightbulb,
  XCircle,
} from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { useFactCheckErrorMessage } from "@/utils/fact-check-error-handler"

import { useCitationPreview } from "../contexts/use-citation-preview"
import { getConfidenceColor, getTrafficLightColor, truncateUrl } from "../utils/fact-check-utils"

interface FactCheckPanelProps {
  isOpen: boolean
  result: FactCheckResult
  onClose: () => void
  sessionId?: string
}

export function FactCheckPanel({ isOpen, result, onClose, sessionId }: FactCheckPanelProps) {
  const { t } = useTranslation()
  const [showFullBreakdown, setShowFullBreakdown] = useState(true)
  const [showAllSources, setShowAllSources] = useState(false)
  const [showAllClaims, setShowAllClaims] = useState(false)

  // Get localized error message
  const localizedErrorMessage = useFactCheckErrorMessage(result.errorCode)

  const { openPreview } = useCitationPreview()

  const breakdownConfig = [
    { key: "coverage" as const, max: 40, label: t("fact_check.breakdown.coverage") },
    { key: "alignment" as const, max: 25, label: t("fact_check.breakdown.alignment") },
    { key: "quality" as const, max: 20, label: t("fact_check.breakdown.quality") },
    { key: "recency" as const, max: 10, label: t("fact_check.breakdown.recency") },
    { key: "consistency" as const, max: 5, label: t("fact_check.breakdown.consistency") },
  ]

  const getStatusIcon = (status: "supported" | "contradicted" | "unclear" | "unable_to_verify") => {
    switch (status) {
      case "supported":
        return <CheckCircle className="text-success size-4" />
      case "contradicted":
        return <XCircle className="text-destructive size-4" />
      case "unclear":
        return <HelpCircle className="text-warning-ink size-4" />
      case "unable_to_verify":
        return <AlertCircle className="text-muted-foreground size-4" />
    }
  }

  const getStatusLabel = (
    status: "supported" | "contradicted" | "unclear" | "unable_to_verify"
  ) => {
    return t(`fact_check.status.${status}`)
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>
            <div className="flex items-center gap-2">
              {t("fact_check.panel_title")}
              <Badge className="bg-primary/20 text-primary border-none" variant="outline">
                BETA
              </Badge>
            </div>
          </SheetTitle>
          <SheetDescription>{t("fact_check.panel_description")}</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Overall Confidence Score */}
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">{t("fact_check.overall_confidence")}</h3>
              <div className="flex items-center gap-2">
                {/* Traffic light indicator */}
                <div
                  className={cn(
                    "border-foreground size-3 rounded-full border",
                    getTrafficLightColor(result.confidence)
                  )}
                />
                <span className={cn("text-3xl font-bold", getConfidenceColor(result.confidence))}>
                  {result.confidence}%
                </span>
              </div>
            </div>

            {/* Natural language summary */}
            <p className="text-muted-foreground mt-2 text-sm">
              {result.confidence >= 80 && t("fact_check.confidence_high")}
              {result.confidence >= 50 &&
                result.confidence < 80 &&
                t("fact_check.confidence_moderate")}
              {result.confidence < 50 && t("fact_check.confidence_low")}
            </p>

            {/* Claims Summary Badges */}
            {result.claims && result.claims.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                {(() => {
                  const supported = result.claims.filter((c) => c.status === "supported").length
                  const unclear = result.claims.filter((c) => c.status === "unclear").length
                  const contradicted = result.claims.filter(
                    (c) => c.status === "contradicted"
                  ).length
                  const unableToVerify = result.claims.filter(
                    (c) => c.status === "unable_to_verify"
                  ).length

                  return (
                    <>
                      {supported > 0 && (
                        <Badge
                          className="border-success bg-success/15 text-foreground gap-1"
                          variant="outline"
                        >
                          <CheckCircle className="size-3" />
                          {supported} Supported
                        </Badge>
                      )}
                      {unclear > 0 && (
                        <Badge
                          className="border-warning-ink/40 bg-warning/30 text-warning-ink gap-1"
                          variant="outline"
                        >
                          <HelpCircle className="size-3" />
                          {unclear} Unclear
                        </Badge>
                      )}
                      {contradicted > 0 && (
                        <Badge
                          className="border-destructive/50 bg-destructive/10 text-destructive gap-1"
                          variant="outline"
                        >
                          <XCircle className="size-3" />
                          {contradicted} Contradicted
                        </Badge>
                      )}
                      {unableToVerify > 0 && (
                        <Badge
                          className="border-border bg-muted/50 text-muted-foreground gap-1"
                          variant="outline"
                        >
                          <AlertCircle className="size-3" />
                          {unableToVerify} Unable to Verify
                        </Badge>
                      )}
                    </>
                  )
                })()}
              </div>
            )}

            {/* Breakdown - Collapsible */}
            <div className="mt-4">
              <Button
                className="w-full justify-between px-0 hover:bg-transparent"
                size="sm"
                variant="ghost"
                onClick={() => setShowFullBreakdown(!showFullBreakdown)}
              >
                <h4 className="text-muted-foreground text-xs font-medium">
                  {t("fact_check.breakdown_title")}
                </h4>
                {showFullBreakdown ? (
                  <ChevronUp className="size-4" />
                ) : (
                  <ChevronDown className="size-4" />
                )}
              </Button>

              {showFullBreakdown && (
                <div className="mt-3 space-y-3">
                  {breakdownConfig.map(({ key, max, label }) => {
                    const score = result.breakdown[key] || 0
                    const percentage = (score / max) * 100

                    // Check if this is recency and it wasn't calculated
                    const isRecencySkipped =
                      key === "recency" && result.breakdown.recency_calculated === false

                    // Color coding based on percentage
                    const getBarColor = (pct: number) => {
                      if (isRecencySkipped) return "bg-muted"
                      if (pct >= 80) return "bg-success-ink"
                      if (pct >= 60) return "bg-warning-ink"
                      if (pct >= 40) return "bg-primary-ink"
                      return "bg-destructive-ink"
                    }

                    return (
                      <div key={key} className="space-y-1.5">
                        {/* Header: Label with score */}
                        <div className="flex items-center justify-between text-sm">
                          <span
                            className={cn(
                              "font-medium",
                              isRecencySkipped ? "text-muted-foreground" : "text-foreground"
                            )}
                          >
                            {label}
                            {isRecencySkipped && (
                              <span className="ml-1.5 text-xs font-normal">
                                ({t("fact_check.skipped")})
                              </span>
                            )}
                          </span>
                          <span
                            className={cn(
                              "text-xs",
                              isRecencySkipped ? "text-muted-foreground" : "text-muted-foreground"
                            )}
                          >
                            {score.toFixed(1)}/{max} {t("fact_check.points")}
                          </span>
                        </div>

                        {/* Progress bar */}
                        <Progress
                          className="h-2"
                          indicatorClassName={getBarColor(percentage)}
                          value={percentage}
                        />
                      </div>
                    )
                  })}

                  {/* Total score */}
                  <div className="mt-4 border-t pt-3">
                    <div className="flex items-center justify-between text-sm font-semibold">
                      <span>{t("fact_check.total")}</span>
                      <span className={cn(getConfidenceColor(result.confidence))}>
                        {result.confidence}/100
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Prompt Quality */}
          {result.promptQuality && (
            <div className="border-primary/20 bg-primary/10 dark:border-primary/20 dark:bg-primary/10 rounded-lg border p-4">
              <div className="mb-2 flex items-center gap-2">
                <Lightbulb className="text-primary dark:text-primary size-4" />
                <h3 className="text-primary dark:text-primary text-sm font-semibold">
                  {t("fact_check.prompt_quality.title")}
                </h3>
              </div>

              {result.promptQuality.ambiguities.length > 0 && (
                <div className="mb-3 space-y-1">
                  <p className="text-primary/80 dark:text-primary/80 text-xs font-medium">
                    {t("fact_check.prompt_quality.ambiguities_found")}
                  </p>
                  <ul className="text-primary/70 dark:text-primary/70 ml-4 space-y-1 text-xs">
                    {result.promptQuality.ambiguities.map((ambiguity, idx) => (
                      <li key={idx} className="list-disc break-words">
                        {ambiguity}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.promptQuality.suggestions && (
                <p className="text-primary/70 dark:text-primary/70 text-xs break-words">
                  <span className="font-medium">{t("fact_check.prompt_quality.suggestion")}:</span>{" "}
                  {result.promptQuality.suggestions}
                </p>
              )}
            </div>
          )}

          {/* Comments */}
          <div className="rounded-lg border p-4">
            <h3 className="mb-2 text-sm font-semibold">{t("fact_check.explanation")}</h3>
            {localizedErrorMessage ? (
              <p className="text-destructive text-sm break-words">{localizedErrorMessage}</p>
            ) : (
              <div className="prose prose-sm text-muted-foreground dark:prose-invert max-w-none text-sm break-words [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{result.comments}</ReactMarkdown>
              </div>
            )}
          </div>

          {/* Sources Used */}
          {result.sourcesUsed.length > 0 && (
            <div className="rounded-lg border p-4">
              <h3 className="mb-2 text-sm font-semibold">{t("fact_check.sources_checked")}</h3>
              <div className="space-y-2">
                {result.sourcesUsed
                  .slice(0, showAllSources ? undefined : 3)
                  .map((source, index) => {
                    const isUrl = source.kind === "url"
                    const isLibrary = source.kind === "library"
                    const isFile = source.kind === "file"
                    const isPreviewable = isLibrary
                    const displayTitle = isUrl ? truncateUrl(source.title) : source.title
                    const isTruncated = isUrl && displayTitle !== source.title

                    // For libraries/files, extract from source data (not title)
                    const colonIndex = displayTitle.indexOf(": ")
                    const libraryName =
                      colonIndex !== -1 ? displayTitle.substring(0, colonIndex) : ""
                    // Remove " (page X)" suffix if present (for uploaded files)
                    let fileName =
                      colonIndex !== -1 ? displayTitle.substring(colonIndex + 2) : displayTitle
                    // Remove " (page X)" format
                    const pageInTitleMatch = fileName.match(/\s*\(page\s+(.+?)\)$/)
                    if (pageInTitleMatch) {
                      fileName = fileName.replace(/\s*\(page\s+.+?\)$/, "")
                    }
                    // Remove "#page=" format (from sourcefile field)
                    fileName = fileName.split("#page=")[0].trim()
                    // Remove trailing " NaN" or any NaN artifacts
                    fileName = fileName.replace(/\s+NaN$/i, "").trim()
                    // Use sourcepage from backend data only for library/file sources
                    const pageNumber =
                      (isLibrary || isFile) && source.sourcepage
                        ? parseInt(source.sourcepage, 10) || null
                        : null

                    const handlePreview = () => {
                      if (isLibrary || isFile) {
                        const citationId = source.library_id
                          ? `${source.library_id}:${source.sourcefile}`
                          : source.sourcefile || ""
                        const citationWithPage =
                          citationId + (pageNumber ? `#page=${pageNumber}` : "")
                        openPreview(citationWithPage, sessionId || "")
                      }
                    }

                    return (
                      <div
                        key={index}
                        role="presentation"
                        className={cn(
                          "border-border/50 bg-muted/30 flex items-center justify-between gap-2 rounded-md border p-2 text-sm transition-colors",
                          isPreviewable && "hover:border-primary/30 cursor-pointer"
                        )}
                        onClick={isPreviewable ? handlePreview : undefined}
                      >
                        <div className="flex min-w-0 flex-1 items-start gap-2">
                          <span className="text-muted-foreground mt-0.5 shrink-0">•</span>
                          {(isLibrary || isFile) && (
                            <div className="mt-0.5 shrink-0">
                              {isLibrary ? (
                                <Book className="text-muted-foreground size-4" />
                              ) : (
                                <FileText className="text-muted-foreground size-4" />
                              )}
                            </div>
                          )}
                          <div className="min-w-0 flex-1 space-y-0.5">
                            {isLibrary || isFile ? (
                              <>
                                <div className="flex items-center gap-2">
                                  <div className="truncate text-xs font-medium" title={fileName}>
                                    {fileName}
                                  </div>
                                  {pageNumber && !isNaN(pageNumber) && (
                                    <div className="text-muted-foreground shrink-0 text-xs">
                                      {t("answer.page")} {pageNumber}
                                    </div>
                                  )}
                                </div>
                                {libraryName && (
                                  <div
                                    className="text-muted-foreground truncate text-xs"
                                    title={libraryName}
                                  >
                                    {libraryName}
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                {(() => {
                                  if (isTruncated) {
                                    return (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <a
                                            className="font-medium break-words hover:underline"
                                            href={source.title}
                                            rel="noopener noreferrer"
                                            target="_blank"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            {displayTitle}
                                          </a>
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-md break-all" side="top">
                                          <p>{source.title}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    )
                                  }
                                  if (isUrl) {
                                    return (
                                      <a
                                        className="font-medium break-words hover:underline"
                                        href={source.title}
                                        rel="noopener noreferrer"
                                        target="_blank"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {displayTitle}
                                      </a>
                                    )
                                  }
                                  return (
                                    <span className="font-medium break-words">{displayTitle}</span>
                                  )
                                })()}
                              </>
                            )}
                            <Badge className="text-xs" variant="outline">
                              {source.kind}
                            </Badge>
                          </div>
                        </div>
                        {isPreviewable && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                aria-label={t("answer.show_document")}
                                className="hover:bg-muted focus-visible:ring-ring shrink-0 rounded p-1 transition-colors focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handlePreview()
                                }}
                              >
                                <Eye className="text-muted-foreground size-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p>{t("answer.show_document")}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    )
                  })}
              </div>
              {result.sourcesUsed.length > 3 && (
                <Button
                  className="mt-2 w-full"
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowAllSources(!showAllSources)}
                >
                  {showAllSources
                    ? t("fact_check.show_less")
                    : t("fact_check.show_more_sources", { count: result.sourcesUsed.length - 3 })}
                </Button>
              )}
            </div>
          )}

          {/* Claims Breakdown */}
          {result.claims && result.claims.length > 0 && (
            <div className="rounded-lg border p-4">
              <h3 className="mb-3 text-sm font-semibold">{t("fact_check.claims_analyzed")}</h3>
              <div className="space-y-3">
                {(() => {
                  // Sort claims to show conflicts first
                  const conflictClaimIds = new Set((result.conflicts || []).map((c) => c.claimId))
                  const sortedClaims = [...result.claims].sort((a, b) => {
                    const aHasConflict = conflictClaimIds.has(a.claimId)
                    const bHasConflict = conflictClaimIds.has(b.claimId)
                    if (aHasConflict && !bHasConflict) return -1
                    if (!aHasConflict && bHasConflict) return 1
                    return 0
                  })
                  return sortedClaims.slice(0, showAllClaims ? undefined : 3)
                })().map((claim) => (
                  <div key={claim.claimId} className="rounded-md border p-3">
                    <div className="mb-2 flex items-start gap-2">
                      {getStatusIcon(claim.status)}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium">
                            {getStatusLabel(claim.status)}
                          </span>
                          <span className="text-muted-foreground text-xs">{claim.score}/100</span>
                        </div>
                        <p className="mt-1 text-sm break-words">{claim.text}</p>
                      </div>
                    </div>
                    {claim.notes && (
                      <div className="prose prose-sm text-muted-foreground dark:prose-invert mt-2 max-w-none text-xs break-words [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{claim.notes}</ReactMarkdown>
                      </div>
                    )}
                    {claim.sources.length > 0 && (
                      <div className="mt-2">
                        <p className="text-muted-foreground text-xs font-medium">
                          {t("fact_check.sources_for_claim")}:
                        </p>
                        <ul className="text-muted-foreground mt-1 ml-4 list-disc text-xs">
                          {claim.sources.map((source, idx) => {
                            const isUrl =
                              source.startsWith("http://") || source.startsWith("https://")
                            const displaySource = isUrl ? truncateUrl(source) : source
                            const isTruncated = isUrl && displaySource !== source

                            return (
                              <li key={idx} className="break-words">
                                {(() => {
                                  if (!isUrl) {
                                    return displaySource
                                  }
                                  if (isTruncated) {
                                    return (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <a
                                            className="cursor-help hover:underline"
                                            href={source}
                                            rel="noopener noreferrer"
                                            target="_blank"
                                          >
                                            {displaySource}
                                          </a>
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-md break-all" side="top">
                                          <p>{source}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    )
                                  }
                                  return (
                                    <a
                                      className="hover:underline"
                                      href={source}
                                      rel="noopener noreferrer"
                                      target="_blank"
                                    >
                                      {displaySource}
                                    </a>
                                  )
                                })()}
                              </li>
                            )
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {result.claims.length > 3 && (
                <Button
                  className="mt-3 w-full"
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowAllClaims(!showAllClaims)}
                >
                  {showAllClaims
                    ? t("fact_check.show_less")
                    : t("fact_check.show_more_claims", { count: result.claims.length - 3 })}
                </Button>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
